import type { ImageSource } from "@/5-shared/types";

/**
 * Unsplash placeholder photo by id.
 *
 * The full URL was written out 31 times across the pages, with the same
 * `auto=format&fit=crop&q=70` tail each time and several ids repeated. Beyond the
 * length, this is the one place to change when the placeholders are replaced with
 * owned assets, which has to happen before production, since every one of these
 * is a hotlink to a third-party origin.
 */
export const unsplash = (id: string, w = 1100, q = 70) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=${q}`;

const WIDTHS = [400, 700, 1100];

/**
 * Responsive image source, Unsplash placeholder or local file.
 *
 * Unsplash: `imageSource({ unsplash: "photo-id" })`
 * Local:    `imageSource({ path: "/milestones/team-2026", ext: "webp" })`
 *
 * Files follow `{path}-{width}.{ext}` naming (one per breakpoint).
 */
export function imageSource(
  ref: { unsplash: string; aspect?: number } | { path: string; ext: string },
  widths = WIDTHS,
): ImageSource {
  if ("unsplash" in ref) {
    const a = ref.aspect ?? 4 / 3;
    return {
      src: `${unsplash(ref.unsplash, widths[1])}&h=${Math.round(widths[1] / a)}`,
      srcSet: widths
        .map((w) => `${unsplash(ref.unsplash, w)}&h=${Math.round(w / a)} ${w}w`)
        .join(", "),
    };
  }
  return {
    src: `${ref.path}-${widths[1]}.${ref.ext}`,
    srcSet: widths.map((w) => `${ref.path}-${w}.${ref.ext} ${w}w`).join(", "),
  };
}
